'use client';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as Sentry from '@sentry/nextjs';
import { Formik, FormikHelpers } from 'formik';
import { useRouter, useSearchParams } from 'next/navigation';

import { CreateDonationRequestModeEnum, Project, Subproject } from '@/app/api';
import { DonationFormBaseValues } from '@/app/components/DonationForm/types';
import { useGoodHabitFormValidationSchema } from '@/app/components/DonationForm/useDonationFormValidationSchema';
import { SelectOption } from '@/app/components/Select/Select';
import { Country, DonationFormType, PaymentMethod } from '@/app/enums';
import useAnalytics from '@/app/hooks/useAnalytics';
import { useToast } from '@/app/hooks/useToast';
import { isResponseError } from '@/app/utils/apiCommonUtils';
import { getCountryConfig } from '@/app/utils/countryConfig';
import { formatPrice } from '@/app/utils/formatting';
import { getDonationAmountIncludingTip } from '@/app/utils/numbers';

import {
  transformCreateDonationViolationsToDonationFormErrors,
  transformGoodHabitDonationFormValuesToCreateDonationRequest,
} from '@/app/components/DonationForm/utils';

import FormCheckbox from '@/app/components/Checkbox/FormCheckbox';
import FormDatePicker from '@/app/components/DatePicker/FormDatePicker';
import Divider from '@/app/components/Divider/Divider';
import AddressFormPart from '@/app/components/DonationForm/donationFormParts/AddressFormPart';
import DonationFormSection from '@/app/components/DonationForm/donationFormParts/DonationFormSection';
import RegularDonorWidget from '@/app/components/DonationForm/donationFormParts/RegularDonorWidget';
import DonationFormDisclaimer from '@/app/components/DonationFormDisclaimer/DonationFormDisclaimer';
import FormCard from '@/app/components/FormCard/FormCard';
import FormContainer from '@/app/components/FormContainer/FormContainer';
import FormInput from '@/app/components/Input/FormInput';
import FormSelect from '@/app/components/Select/FormSelect';

import { CustomAmount } from '@/app/components/DonationForm/donationFormParts/CustomAmount';
import { DonationMatchingWidget } from '@/app/components/DonationForm/donationFormParts/DonationMatchingWidget';
import { SubmitButton } from '@/app/components/DonationForm/donationFormParts/SubmitButton';
import { TipFormSection } from '@/app/components/DonationForm/donationFormParts/TipFormSection';
import { PaymentMethodFormType } from '@/app/hooks/usePaymentMethods';
import { useAbGroup } from '@/app/providers/ConfigProvider';
import { useCheckoutPageViewAnalytics } from '@/app/hooks/useAnalytics/events/donation/donation-view';
import dynamic from 'next/dynamic';
import { PaymentMethodsImportLoader } from '@/app/components/PaymentMethods/ImportLoader';
import { useCurrentUser } from '@/app/hooks/useCurrentUser';
import { useCategoriesOptions } from '@/app/components/DonationForm/GoodHabitDonationForm/use-categories-options';
import { usePostCreateDonation } from '@/src/api/client';
import { getSessionStorageByKeys } from '@/app/utils/getSessionStorageByKeys';
import { UTM_KEYS } from '@/app/constants';
import { removeFromSessionStorage } from '@/app/utils/removeFromSessionStorage';

const PaymentMethods = dynamic(() => import('@/app/components/PaymentMethods/PaymentMethods'), {
  ssr: false,
  loading: PaymentMethodsImportLoader,
});

interface GoodHabitDonationFormBaseFormValues extends DonationFormBaseValues {
  categoryId?: string;
}

export interface GoodHabitDonationFormValuesAuthenticatedUser extends GoodHabitDonationFormBaseFormValues {
  formType: DonationFormType.GOOD_HABIT_AUTHENTICATED_USER;
}

export interface GoodHabitDonationFormValuesUnauthenticatedUser extends GoodHabitDonationFormBaseFormValues {
  formType: DonationFormType.GOOD_HABIT_UNAUTHENTICATED_USER;
  email: string;
}

interface RegularDonorFormProps<
  FormValuesType extends DonationFormBaseValues = GoodHabitDonationFormValuesUnauthenticatedUser,
> {
  project: Project;
  subproject?: Subproject;
  initialValues?: Partial<FormValuesType>;
  countrySelectOptions: SelectOption<Country>[];
  isCategoriesSelectVisible?: boolean;
  onCloseButtonClick?: () => void;
  onSuccess?: (donationId: string) => void;
  onFailure?: () => void;
}

const countryConfig = getCountryConfig();

function GoodHabitDonationForm<
  FormValuesType extends GoodHabitDonationFormBaseFormValues = GoodHabitDonationFormValuesUnauthenticatedUser,
>({
  project,
  initialValues: defaultInitialValues,
  countrySelectOptions,
  isCategoriesSelectVisible: initialIsCategoriesSelectVisible,
  onCloseButtonClick,
  onSuccess,
  onFailure,
  subproject,
}: RegularDonorFormProps<FormValuesType>) {
  const { t } = useTranslation();
  const router = useRouter();
  const {
    donation: { pushDonationCompletedEvent },
  } = useAnalytics();
  const { currentUser, canFetchUser, isLoading } = useCurrentUser();
  const {
    categoryOptions,
    getCategoryImage,
    getCategoryLabel,
    isLoading: isLoadingCategories,
  } = useCategoriesOptions();

  const isLoadingData = (canFetchUser && isLoading) || isLoadingCategories;
  const searchParams = useSearchParams();
  const { showErrorToast, showGenericErrorToast } = useToast();
  const [isAmountInputVisible, setIsAmountInputVisible] = useState(!defaultInitialValues?.amount);
  const [isCategoriesSelectVisible, setIsCategoriesSelectVisible] = useState(initialIsCategoriesSelectVisible ?? false);
  useCheckoutPageViewAnalytics(PaymentMethodFormType.GoodHabit);

  const { mutateAsync: createDonationAsync } = usePostCreateDonation();

  const isTipDisabled = useMemo(() => project.settings.hasTipsDisabled, [project.settings.hasTipsDisabled]);
  const abGroup = useAbGroup();
  const isFirstNameRequired = !currentUser?.firstName;
  const isLastNameRequired = !currentUser?.lastName;
  const isPhoneNumberRequired = project.settings.hasPhoneNumberRequired;

  const validationSchema = useGoodHabitFormValidationSchema({
    isAuthenticated: !!currentUser,
    isFirstNameRequired,
    isLastNameRequired,
    isTipDisabled,
    isPhoneNumberRequired,
  });

  const initialValues = useMemo<
    GoodHabitDonationFormValuesAuthenticatedUser | GoodHabitDonationFormValuesUnauthenticatedUser
  >(() => {
    const nameOnReceipt =
      currentUser?.firstName && currentUser?.lastName
        ? `${currentUser?.firstName} ${currentUser?.lastName}`
        : currentUser?.fullName;

    const urlAmount = searchParams.get('amount');
    const defaultAmount = urlAmount && !isNaN(Number(urlAmount)) ? Number(urlAmount) : undefined;

    const base = {
      donationMode: CreateDonationRequestModeEnum.LongTerm,
      categoryId: searchParams.get('categoryId') ?? undefined,
      paymentMethod: countryConfig.goodHabit.enabled
        ? countryConfig.goodHabit.defaultPaymentMethod
        : PaymentMethod.Card,
      // We need an initial value in order not to change component from uncontrolled to controlled.
      // Null (or undefined) is not an option and "0" would result in displaying "0" in the input; we just need an empty input
      amount: defaultAmount as any,
      firstName: currentUser?.firstName ?? '',
      lastName: currentUser?.lastName ?? '',
      certificateOfGoodness: false,
      donationReceipt: currentUser?.wantsDonationReceipt ?? false,
      noNewsletter: false,
      agreedToShareContact: false,
      certificateRecipient: '',
      certificateDonor: '',
      nameOnReceipt: nameOnReceipt ?? '',
      phoneNumber: currentUser?.phoneNumber ?? '',
      streetAndHouseNumber: currentUser?.address?.street ?? '',
      city: currentUser?.address?.city ?? '',
      zipCode: currentUser?.address?.zip ?? '',
      tin: currentUser?.companyLegalId ?? '',
      birthDate: currentUser?.birthdate ? new Date(currentUser.birthdate).toISOString() : undefined,
      // Workaround for the input not to swtich from uncontrolled to controlled (has to have a value)
      customTipAmount: '' as any as number,
      tipPercentage: project.settings.hasTipsDisabled
        ? 0
        : countryConfig.donationFormCommon.defaultTipForAmountBelowBreakpoint,
      country: (currentUser?.address?.country as Country) ?? countryConfig.country,
    };

    if (!currentUser) {
      return {
        ...base,
        email: '',
        ...(defaultInitialValues ?? {}),
        formType: DonationFormType.GOOD_HABIT_UNAUTHENTICATED_USER,
      };
    }

    return {
      ...base,
      ...(defaultInitialValues ?? {}),
      formType: DonationFormType.GOOD_HABIT_AUTHENTICATED_USER,
    };
  }, [defaultInitialValues, currentUser, isLoadingData]);

  const handleRegularDonorWidgetEditButtonClick = useCallback(() => {
    setIsAmountInputVisible(true);
    setIsCategoriesSelectVisible(true);
  }, [setIsAmountInputVisible, setIsCategoriesSelectVisible]);

  const handleSubmit = useCallback(
    async (
      values: GoodHabitDonationFormValuesAuthenticatedUser | GoodHabitDonationFormValuesUnauthenticatedUser,
      helpers: FormikHelpers<
        GoodHabitDonationFormValuesAuthenticatedUser | GoodHabitDonationFormValuesUnauthenticatedUser
      >,
    ) => {
      try {
        const transformedValues = transformGoodHabitDonationFormValuesToCreateDonationRequest(
          project.id,
          values,
          abGroup,
          currentUser?.id,
        );
        debugger;

        const utm = getSessionStorageByKeys(UTM_KEYS);

        const response = await createDonationAsync({
          data: {
            ...transformedValues,
            utm: utm,
          },
        });

        removeFromSessionStorage(UTM_KEYS)

        // This should never happen; null can only return a dry run.
        if (response === null || response === undefined) {
          throw new Error('Response should not be null or undefined at this point.');
        }

        onSuccess?.(response.id);

        if (values.paymentMethod !== PaymentMethod.Card) {
          pushDonationCompletedEvent({
            donationId: response.id,
            requestDto: transformedValues,
            project,
            paymentMethod: values.paymentMethod,
          });
        }

        const baseUrl = process.env.NEXT_PUBLIC_REDIRECT_URL;
        const queryString = values.paymentMethod === PaymentMethod.Card ? '?redirectToGateway=1' : '';

        if (baseUrl?.endsWith('/')) {
          return router.push(`${baseUrl}prispevek/detail/${response.id}${queryString}`);
        }

        router.push(`${baseUrl}/prispevek/detail/${response.publicId}${queryString}`);
      } catch (error: unknown) {
        const isValidationError = isResponseError(error) && error.response.status === 422;

        if (!isValidationError) {
          Sentry.captureException(error);
        }

        onFailure?.();

        if (!isResponseError(error)) {
          return showGenericErrorToast();
        }

        switch (error.response.status) {
          case 422:
            const responseJson = await error.response.json();
            const transformedErrors = transformCreateDonationViolationsToDonationFormErrors(responseJson.violations);
            helpers.setErrors(transformedErrors);

            return showErrorToast(t('messages.422'));

          default:
            showGenericErrorToast();
        }
      }
      helpers.setSubmitting(false);
    },
    [project.id, onSuccess, onFailure, router, showErrorToast, showGenericErrorToast, currentUser, t],
  );

  if (isLoadingData) {
    return null; // or a loading spinner
  }

  return (
    <div className="w-full">
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, values, touched, setFieldValue, errors }) => (
          <FormCard onCloseButtonClick={onCloseButtonClick}>
            <FormContainer>
              <h1 className="w-full text-center inline-block text-[24px] lg:text-[32px] leading-[32px] lg:leading-[40px] font-medium text-neutral-dark mb-[24px]">
                {t('components.DonationForm.regularDonor')}
              </h1>
              <DonationFormSection order={1} title={t('components.DonationForm.donation')}>
                {(!isCategoriesSelectVisible || !isAmountInputVisible) && (
                  <RegularDonorWidget
                    amount={values.amount ?? undefined}
                    categoryLabel={getCategoryLabel(values.categoryId) ?? undefined}
                    isAmountFilledIn={!isAmountInputVisible}
                    image={getCategoryImage(values.categoryId)}
                    onEditButtonClick={handleRegularDonorWidgetEditButtonClick}
                    className="mb-[16px]"
                  />
                )}
                {isCategoriesSelectVisible && (
                  <FormSelect
                    name="categoryId"
                    label={t('components.DonationForm.donationField')}
                    options={categoryOptions}
                  />
                )}
                {isAmountInputVisible && (
                  <CustomAmount
                    currentTip={values.tipPercentage}
                    className="mb-[8px]"
                    label={t('components.DonationForm.anotherAmount')}
                    isTipTouched={!!touched.tipPercentage}
                    onTipChange={([fieldName, value]) => setFieldValue(fieldName, value)}
                  />
                )}
                <div className="text-balance text-[13px] text-center relative p-[24px] rounded mt-[16px] bg-primary-light before:top-[-8px] before:content-[' '] before:absolute before:w-[0] before:h-[0] before:left-[50%] before:border-t-[0] before:border-r-[8px] before:border-b-[8px] before:border-l-[8px] before:border-t-[transparent] before:border-r-[transparent] before:border-b-primary-light before:border-l-[transparent] before:translate-x-[-50%] drop-shadow-filter">
                  {t('components.DonationForm.hundredPercentOfYourDonationNote')}
                </div>
              </DonationFormSection>
              <DonationFormSection order={2} title={t('components.DonationForm.paymentType')}>
                {project.donationMatching && !subproject && (
                  <DonationMatchingWidget
                    currentDonationValue={values.amount}
                    donationMatching={project.donationMatching}
                  />
                )}

                <PaymentMethods formType={PaymentMethodFormType.GoodHabit} />
              </DonationFormSection>
              <Divider className="mb-[56px] mt-[64px]" />
              <DonationFormSection
                order={3}
                title={t('components.DonationForm.donorInfo')}
                description={t('components.DonationForm.thisInfoIsPrivate')}
              >
                {!currentUser && (
                  <FormInput
                    name="email"
                    label={t('components.DonationForm.email')}
                    note={t('components.DonationForm.emailNote')}
                  />
                )}

                {isFirstNameRequired && (
                  <FormInput
                    name="firstName"
                    label={t('components.DonationForm.firstName')}
                    onBlur={() => {
                      if (touched.nameOnReceipt) {
                        return;
                      }

                      const fullName = [values.firstName ?? undefined, values.lastName ?? undefined]
                        .filter(Boolean)
                        .join(' ');

                      setFieldValue('nameOnReceipt', fullName);
                    }}
                  />
                )}

                {isLastNameRequired && (
                  <FormInput
                    name="lastName"
                    label={t('components.DonationForm.lastName')}
                    onBlur={() => {
                      if (touched.nameOnReceipt) {
                        return;
                      }

                      const fullName = [values.firstName ?? undefined, values.lastName ?? undefined]
                        .filter(Boolean)
                        .join(' ');

                      setFieldValue('nameOnReceipt', fullName);
                    }}
                  />
                )}

                <span className="block mb-[8px] font-medium text-[16px] leading-[24px]">
                  {t('components.DonationForm.whatYouGet')}
                </span>

                <FormCheckbox
                  name="donationReceipt"
                  label={t('components.DonationForm.donationReceiptCheckbox')}
                  note={t('components.DonationForm.donationReceiptNote')}
                />

                {values.donationReceipt && (
                  <div>
                    <FormInput name="nameOnReceipt" label={t('components.DonationForm.nameOnReceipt')} />

                    {isPhoneNumberRequired && (
                      <FormInput
                        name="phoneNumber"
                        label={t('components.DonationForm.phoneNumberLabel')}
                        placeholder={countryConfig.donationFormCommon.phoneNumberFieldPlaceholder}
                      />
                    )}

                    <AddressFormPart countrySelectOptions={countrySelectOptions} />

                    <FormInput
                      name="tin"
                      label={t('components.DonationForm.tin')}
                      note={t('components.DonationForm.tinNote')}
                    />

                    <FormDatePicker name="birthDate" label={t('components.DonationForm.birthDate')} />
                  </div>
                )}
                {!currentUser?.subscribesToNewsletter && (
                  <FormCheckbox name="noNewsletter" label={t('components.DonationForm.noNewsletterCheckbox')} />
                )}
              </DonationFormSection>
              {!isTipDisabled && (
                <TipFormSection
                  allowAAmountBasedTip={false}
                  amount={values.amount}
                  sectionTitle={t('components.DonationForm.supportDonio')}
                  onTipPercentageChange={() => setFieldValue('customTipAmount', '')}
                />
              )}
              <SubmitButton isSubmitting={isSubmitting}>
                {t('components.DonationForm.submitRegularDonation', {
                  amount: formatPrice(
                    getDonationAmountIncludingTip(
                      values.amount || 0,
                      values.tipPercentage || 0,
                      values.customTipAmount,
                    ),
                  ),
                })}
              </SubmitButton>
              <DonationFormDisclaimer />
            </FormContainer>
          </FormCard>
        )}
      </Formik>
    </div>
  );
}

export default GoodHabitDonationForm;
